import useRisksmartUser, {
  type Roles,
} from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { useQuery } from '@tanstack/react-query';

import { type Starter, STATIC_STARTERS } from './staticStarters';

const DEFAULT_COUNT = 4;
const REQUEST_TIMEOUT_MS = 3000;

interface StartersResponse {
  starters: Starter[];
}

interface FetchStartersArgs {
  baseUrl: string;
  token: string;
  module: string | null;
  role: Roles | undefined;
  count: number;
  signal?: AbortSignal;
}

const fetchStarters = async ({
  baseUrl,
  token,
  module,
  role,
  count,
  signal,
}: FetchStartersArgs): Promise<Starter[]> => {
  const params = new URLSearchParams({ count: String(count) });
  if (module) {
    params.set('module', module);
  }
  if (role) {
    params.set('role', role);
  }

  const url = `${baseUrl.replace(/\/$/, '')}/ai-engine/chat-api/starters?${params}`;

  const response = await fetch(url, {
    signal,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`starters fetch failed: ${response.status}`);
  }

  const data = (await response.json()) as StartersResponse;
  if (!Array.isArray(data?.starters) || data.starters.length === 0) {
    throw new Error('starters response had no starters');
  }

  return data.starters;
};

/**
 * Fetches dynamic conversation starters for the AI Assistant from the
 * ai-engine `/starters` endpoint. On any failure (network, 401, 422,
 * timeout, malformed response, missing config) returns the static
 * placeholder set so the consumer always has something to render.
 *
 * The query key intentionally excludes any session id — rotation comes
 * from natural unmount/remount when the chat side panel closes (see
 * `useSidePanelStore`), combined with the server-side per-request
 * random selection.
 */
export const useDynamicStarters = (
  module: string | null,
  role: Roles | undefined,
  count: number = DEFAULT_COUNT
): { starters: readonly Starter[]; source: 'dynamic' | 'static' } => {
  const { getAccessTokenSilently, isAuthenticated } = useRisksmartUser();

  const query = useQuery<readonly Starter[]>({
    queryKey: ['ai-starters', module, role, count],
    queryFn: async () => {
      const baseUrl = getEnv('REACT_APP_AI_CHAT_API_URL', true);
      if (!baseUrl) {
        return STATIC_STARTERS;
      }

      const token = await getAccessTokenSilently();
      if (!token) {
        return STATIC_STARTERS;
      }

      try {
        return await fetchStarters({
          baseUrl,
          token,
          module,
          role,
          count,
          signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
        });
      } catch {
        return STATIC_STARTERS;
      }
    },
    enabled: isAuthenticated,
    placeholderData: STATIC_STARTERS,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const starters = query.data ?? STATIC_STARTERS;
  const source: 'dynamic' | 'static' =
    starters === STATIC_STARTERS ? 'static' : 'dynamic';

  return { starters, source };
};
